import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  controlToastState,
  Footer,
  openModal,
  SpinningLoader,
} from "risa-oasis-ui_v2";
import { postRequestForCoverageRefresh } from "../../../api/postCall/refreshCoverage";
import { updateCigBoxNote } from "../../../api/postCall/updateCigBoxNote";
import ApproveEvbv from "../../../components/modals/approveEvbv/approveEvbv";
import EvBvModal from "../../../components/modals/ebvbModal/EvbvModal";
import SigBoxNoteModal from "../../../components/modals/sigBoxNoteModal/sigBoxNoteModal";
import ValidateEvModal from "../../../components/modals/validateEvModal/ValidateEvModal";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { CoverageDetailsData } from "../../../data-model/coverage-details";
import { EVWriteBackStatus } from "../../../enums/evBvWriteBackStatus";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import {
  financialStatusOptions,
  MedicalPaOrderStatus,
} from "../../../enums/medicalPaOrderStatus";
import { ModalId } from "../../../enums/modalId";
import { getMedicalPaCoveragerawResponse } from "../../../redux/slice/medicalPaCoveragerawResponseSlice";
import { setOpenedModalId } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import RefreshIcon from "../../../svg/refresh-icon";
import { logError } from "../../../utils/customLogger";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { getAuthStatusOfMedicalPaOrder } from "../../../utils/getAuthStatusOfMedicalPaOrder";
import { getItemFromLocalStorage } from "../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { Coverage } from "../components/coverage/coverage";
import { generateCoverageInfoForMedicalPaOrder } from "../helpers/generateCoverageInforForMedicalPaOrder";

interface CoverageDetailsProps {
  id?: string;
}

export const CoverageDetails = (props: CoverageDetailsProps) => {
  const id = props.id ? props.id : undefined;

  const [coverage, setCoverage] = React.useState<CoverageDetailsData[] | null>(
    null,
  );

  const [isApproveEvbvModalOpen, setIsApproveEvbvModalOpen] =
    React.useState<boolean>(false);

  const [activeTabIndex, setActiveTabIndex] = React.useState<number>(0);
  const [showEvBvModal, setShowEvBvModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [isRefreshingCoverage, setIsRefreshingCoverage] = useState(false);
  const [isSigBoxNoteModalOpen, setIsSigBoxNoteModalOpen] = useState(false);
  const [createFinalNote] = useState("");
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector((state: RootState) => state.modal);

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const org_id = getOrgIdForFetchExternalWorklist();

  useEffect(() => {
    if (id) {
      dispatch(getMedicalPaCoveragerawResponse(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (openedModalId === ModalId.INSURANCE_ERROR_DIALOG) {
      openModal(ModalId.INSURANCE_ERROR_DIALOG);
    }
  }, [openedModalId]);

  const getCurrentStatusBadge = useMemo(() => {
    const currentStatus =
      singleMedicalPaOrder?.status?.financial_review ??
      MedicalPaOrderStatus.NEW;
    const currentStatusBadge = financialStatusOptions.find(
      (option) => option.id === currentStatus,
    );
    return currentStatusBadge;
  }, [singleMedicalPaOrder]);

  const checkIfSubmissionPageShouldBeShown = () => {
    const getAuthStatus = getAuthStatusOfMedicalPaOrder(
      singleMedicalPaOrder ?? {},
      authStatusOptions,
    ).id;

    if (getAuthStatus === MedicalPaOrdersAuthStatus.AuthOnFile) {
      return false;
    }

    if (getAuthStatus === MedicalPaOrdersAuthStatus.AuthRequired) {
      return (
        singleMedicalPaOrder?.status?.submission !==
        MedicalPaOrderStatus.PAYER_NOT_SUPPORTED
      );
    }

    return false;
  };

  // const checkIfNoteShouldBeAddedToCigBoxData = () => {
  //   if (
  //     deductiblesTotals === 0 &&
  //     deductiblesUsed === 0 &&
  //     outOfPocketTotals === 0 &&
  //     outOfPocketUsed === 0 &&
  //     coverage?.[activeTabIndex]?.coInsurance === "0%"
  //   ) {
  //     return false;
  //   }
  //   return true;
  // };

  const navigate = useNavigate();

  useEffect(() => {
    if (singleMedicalPaOrder) {
      const generatedCoverage =
        generateCoverageInfoForMedicalPaOrder(singleMedicalPaOrder);

      setCoverage(generatedCoverage as CoverageDetailsData[]);
    }
  }, [singleMedicalPaOrder]);

  useEffect(() => {
    if (isApproveEvbvModalOpen) {
      openModal("approve-evbv-modal");
    }
  }, [isApproveEvbvModalOpen]);

  const submitButtonText = useMemo(() => {
    if (coverage?.[0]?.isActive === "inactive") {
      return "Mark as Completed";
    }
    return "Continue";
  }, [coverage]);

  const shouldOpenHappyModal = useMemo(() => {
    const evWriteBackStatusForHappyModal = [
      EVWriteBackStatus.SUCCESS,
      EVWriteBackStatus.IN_PROGRESS,
    ];

    const evStatus = singleMedicalPaOrder?.status?.ev_write_back_status;

    if (!evStatus) return false;

    return evWriteBackStatusForHappyModal.includes(
      evStatus as EVWriteBackStatus,
    );
  }, [
    singleMedicalPaOrder?.status?.ev_write_back_status,
    singleMedicalPaOrder?.id,
  ]);

  const handleSubmitClick = () => {
    if (coverage?.[0]?.isActive === "active") {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id,
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: org_id ?? "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name: EventsName.CONTINUE_BUTTON_CLICKED,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
          patient_name:
            singleMedicalPaOrder?.demographics?.first_name +
            " " +
            singleMedicalPaOrder?.demographics?.last_name,
          patient_mrn: singleMedicalPaOrder?.demographics?.patient_mrn ?? "",
          payer_name: payerName,
        },
      });
    }
    if (coverage?.[0]?.isActive === "inactive") {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id,
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: org_id ?? "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name: EventsName.RAISE_QUERY_MODAL_OPENED,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
          patient_name:
            singleMedicalPaOrder?.demographics?.first_name +
            " " +
            singleMedicalPaOrder?.demographics?.last_name,
          source: ModalNamesForBigQuery.RAISE_QUERY_MODAL,
          patient_mrn: singleMedicalPaOrder?.demographics?.patient_mrn ?? "",
          payer_name: payerName,
        },
      });
      if (shouldOpenHappyModal) {
        dispatch(setOpenedModalId({ id: ModalId.HAPPY_FLOW_MODAL }));
      } else {
        dispatch(
          setOpenedModalId({
            id: ModalId.COMMENT_RPA_MODAL_NEW,
          }),
        );
      }
      return;
    }
    navigate(`/worklists/singleOrder/${singleMedicalPaOrder?.id}`);
  };

  useEffect(() => {
    if (showEvBvModal) {
      openModal("evbv-modal");
    }
  }, [showEvBvModal]);

  useEffect(() => {
    if (showValidateModal) {
      openModal("validate-ev-modal");
    }
  }, [showValidateModal]);

  const handleCoverageRefreshAction = async () => {
    setIsRefreshingCoverage(true);
    try {
      await postRequestForCoverageRefresh(singleMedicalPaOrder?.id ?? "");
      controlToastState("coverage-refresh-success");
    } catch (error) {
      logError(error as Error, "Error while refreshing coverage");
      controlToastState("coverage-refresh-failure");
    } finally {
      setIsRefreshingCoverage(false);
    }
  };

  // const handleCigBoxDataUpdate = (note: string) => {
  //   const getServiceYearFrom =
  //     singleMedicalPaOrder?.coverage?.primary?.patient_responsibility
  //       ?.service_year_from;
  //   const additionalDetailsInNote = `Date BV: ${moment().format(
  //     "MM/DD/YYYY",
  //   )}, Initials: Authmate Case, As per Insurance, ${singleMedicalPaOrder?.coverage?.primary?.payer_name ?? ""} - ${singleMedicalPaOrder?.coverage?.primary?.patient_responsibility?.coverage_status ?? ""} ${getServiceYearFrom && getServiceYearFrom !== "-" ? `(${moment(singleMedicalPaOrder?.coverage?.primary?.patient_responsibility?.service_year_from).format("MM/DD/YYYY")})` : ""}`;
  //   const finalNote = checkIfNoteShouldBeAddedToCigBoxData()
  //     ? `${note}\n${additionalDetailsInNote}`
  //     : additionalDetailsInNote;
  //   setCreateFinalNote(finalNote);
  //   setIsSigBoxNoteModalOpen(true);
  // };

  const handleAddToSigBox = (note: string) => {
    const bodyToSend = {
      note: note,
      patient_mrn: singleMedicalPaOrder?.demographics?.patient_mrn ?? "",
      drug_name: singleMedicalPaOrder?.payload?.regimen_name ?? "",
      identifier: singleMedicalPaOrder?.id ?? "",
    };
    try {
      updateCigBoxNote(bodyToSend);
      controlToastState("cig-box-note-updated-success");
    } catch (error) {
      logError(error as Error, "Error while updating CIG box note");
      controlToastState("cig-box-note-updated-failure");
    }
  };

  useEffect(() => {
    if (isSigBoxNoteModalOpen) {
      openModal("sig-box-note-modal");
    }
  }, [isSigBoxNoteModalOpen]);

  return (
    <>
      <div className="coverage-details--container flex h-full w-full flex-col overflow-hidden">
        <div className="coverage-details--header-with-body flex flex-1 flex-col gap-4 overflow-hidden px-4 py-2">
          <div className="coverage-details--main-header flex items-center justify-between">
            <div className="coverage-details--header-details flex items-center justify-center gap-2">
              <div className="coverage-details--patient-name text-h16 font-semibold leading-6 text-primaryGray-1">
                {generateFullName(
                  singleMedicalPaOrder?.demographics?.first_name ?? "",
                  "",
                  singleMedicalPaOrder?.demographics?.last_name ?? "",
                )}
              </div>
              <div className="coverage-details--page-name text-sm font-semiBold text-black">
                ({singleMedicalPaOrder?.demographics?.patient_mrn ?? ""})
              </div>
            </div>
            <div className="manual-visit-type-and-cpt-container">
              <div
                className="authorization--header__mark-error text-small font-semibold text-[#0056D6] hover:cursor-pointer hover:text-blue-500"
                onClick={() => {
                  setShowValidateModal(true);

                  apiWorkerManager.enqueueAndProcess({
                    patient_id: singleMedicalPaOrder?.patient_id,
                    order_id: singleMedicalPaOrder?.order_id ?? "",
                    org_id: org_id ?? "",
                    user_id: user?.id ?? "",
                    user_email: user?.email ?? "",
                    event_name: EventsName.VALIDATE_EV_CLICKED,
                    additional_data: {
                      screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,

                      patient_name:
                        singleMedicalPaOrder?.demographics?.first_name +
                        " " +
                        singleMedicalPaOrder?.demographics?.last_name,
                      payer_name: payerName,
                    },
                  });
                }}
              >
                Validate
              </div>
              <div
                className="authorization--header__mark-error text-small font-semibold text-[#CD0202] hover:cursor-pointer hover:text-red-500"
                onClick={() => {
                  setShowEvBvModal(true);
                  apiWorkerManager.enqueueAndProcess({
                    patient_id: singleMedicalPaOrder?.patient_id,
                    order_id: singleMedicalPaOrder?.order_id ?? "",
                    org_id: org_id ?? "",
                    user_id: user?.id ?? "",
                    user_email: user?.email ?? "",
                    event_name: EventsName.REPORT_INACCURACY_CLICKED,
                    additional_data: {
                      screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                      patient_name:
                        singleMedicalPaOrder?.demographics?.first_name +
                        " " +
                        singleMedicalPaOrder?.demographics?.last_name,
                      payer_name: payerName,
                    },
                  });
                }}
              >
                Report Inaccuracy
              </div>

              <div
                className="refresh-icon flex h-[1.375rem] w-[1.375rem] items-center justify-center rounded-[0.1375rem] bg-[#F7F9FA] hover:scale-105 hover:cursor-pointer"
                onClick={() => {
                  if (!isRefreshingCoverage) {
                    handleCoverageRefreshAction();
                    apiWorkerManager.enqueueAndProcess({
                      patient_id: singleMedicalPaOrder?.patient_id,
                      order_id: singleMedicalPaOrder?.order_id ?? "",
                      org_id: org_id ?? "",
                      user_id: user?.id ?? "",
                      user_email: user?.email ?? "",
                      event_name:
                        EventsName.REFRESH_BUTTON_CLICKED +
                        " (Coverage Screen)",
                      additional_data: {
                        screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                        patient_name:
                          singleMedicalPaOrder?.demographics?.first_name +
                          " " +
                          singleMedicalPaOrder?.demographics?.last_name,
                        payer_name: payerName,
                      },
                    });
                  }
                }}
              >
                {isRefreshingCoverage ? <SpinningLoader /> : <RefreshIcon />}
              </div>
            </div>
          </div>
          {/* This is the AI Comment section which is not used in the current implementation */}
          {/* {coverage?.[activeTabIndex]?.isActive === "active" && (
            <>
              <div className="coverage-details--ai-comment">
                <AiComment
                  text={`Note: Pt is Respo for DD $${deductiblesTotals} / $${deductiblesUsed} MET, OOP $${outOfPocketTotals} / $${outOfPocketUsed} MET & ${coverage?.[activeTabIndex]?.coInsurance} Co ins`}
                  status={true}
                  subTextType="bold"
                  onUpdateCigBoxData={handleCigBoxDataUpdate}
                />
              </div>
            </>
          )}
          {coverage?.[activeTabIndex]?.isActive === "inactive" && (
            <>
              <div className="coverage-details--ai-comment">
                <AiComment
                  text={`The insurance is Inactive`}
                  status={false}
                  subTextType="bold"
                  onUpdateCigBoxData={handleCigBoxDataUpdate}
                />
              </div>
            </>
          )} */}

          <div className="coverage-details--body flex-1 overflow-hidden">
            {coverage && (
              <Coverage
                coverageData={coverage}
                currentTabIndex={(index) => setActiveTabIndex(index)}
              />
            )}
          </div>
        </div>
        <div className="coverage-details--footer">
          <Footer
            cancelButtonText={"Go Back"}
            submitButtonText={submitButtonText}
            onCancelClick={() => {
              const parentTable =
                getItemFromLocalStorage(LocalStorageKeys.PARENT_TABLE) ?? "";
              navigate(`/worklists/${parentTable}`);
              apiWorkerManager.enqueueAndProcess({
                patient_id: singleMedicalPaOrder?.patient_id,
                order_id: singleMedicalPaOrder?.order_id ?? "",
                org_id: org_id ?? "",
                user_id: user?.id ?? "",
                user_email: user?.email ?? "",
                event_name: EventsName.GO_BACK_BUTTON_CLICKED,
                additional_data: {
                  screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                  source: ScreenNamesForBigQuery.COVERAGE_SCREEN,
                  payer_name: payerName,
                },
              });
            }}
            onSubmitClick={handleSubmitClick}
            isDisabled={false}
          />
        </div>
        {showEvBvModal && (
          <EvBvModal
            onClose={() => {
              setShowEvBvModal(false);
            }}
            orderData={singleMedicalPaOrder}
            activeTabIndex={activeTabIndex}
          />
        )}
        {showValidateModal && (
          <ValidateEvModal
            onClose={() => {
              setShowValidateModal(false);
            }}
            id={singleMedicalPaOrder?.id || ""}
            activeTabIndex={activeTabIndex}
          />
        )}
      </div>
      {isApproveEvbvModalOpen && (
        <ApproveEvbv
          onClose={() => setIsApproveEvbvModalOpen(false)}
          badgeList={financialStatusOptions.sort((a, b) =>
            a.id.localeCompare(b.id),
          )}
          selectedBadge={getCurrentStatusBadge}
          orderId={singleMedicalPaOrder?.id ?? ""}
          shouldNavigateToSubmission={checkIfSubmissionPageShouldBeShown()}
        />
      )}
      {isSigBoxNoteModalOpen && (
        <SigBoxNoteModal
          notes={createFinalNote}
          lastUpdateStatus={
            singleMedicalPaOrder?.status?.ev_write_back_status ??
            EVWriteBackStatus.NOT_INITIATED
          }
          onClose={() => {
            setIsSigBoxNoteModalOpen(false);
          }}
          onSave={handleAddToSigBox}
        />
      )}
    </>
  );
};
